import React, { useRef, useState } from "react";
import { Plus, Trash2, GripVertical, ChevronUp, ChevronDown, Star, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import { cn } from "@/lib/utils";
import { MS_STATUS, OWNER_OPTIONS, PRIORITY_META } from "@/lib/careerBuilder";
import { WsSection } from "@/components/negotiation/workspace/shared";
import { translateFr as tfr } from "@/i18n";

const uid = (p) => `${p}_${Math.random().toString(36).slice(2, 8)}`;

/* ---------- STEP 3 — Roadmap timeline builder (cards + drag & drop) ---------- */
export function StepRoadmap({ draft, update }) {
  const milestones = draft.milestones || [];
  const dragIndex = useRef(null);
  const [overIndex, setOverIndex] = useState(null);

  const setList = (list) => update(["milestones"], list);
  const patch = (i, key, val) => setList(milestones.map((m, k) => (k === i ? { ...m, [key]: val } : m)));
  const move = (from, to) => {
    if (to < 0 || to >= milestones.length || from === to) return;
    const list = [...milestones];
    const [it] = list.splice(from, 1);
    list.splice(to, 0, it);
    setList(list);
  };

  return (
    <div data-testid="builder-step-roadmap">
      <WsSection title={tfr("Feuille de route")} subtitle={tfr("Réordonnez par glisser-déposer ou avec les flèches. La timeline du PDF se met à jour en direct.")}
        right={
          <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs"
            onClick={() => setList([...milestones, { id: uid("bm"), title: "", target_month: (milestones.at(-1)?.target_month || 0) + 6, status: "upcoming", is_key: false }])}
            data-testid="roadmap-add-btn">
            <Plus className="h-3.5 w-3.5" /> Ajouter un jalon
          </Button>
        }>
        <div className="space-y-2.5">
          {milestones.length === 0 && (
            <p className="text-sm text-muted-foreground rounded-xl border border-dashed border-border p-4 text-center">
              {tfr("Aucun jalon — ajoutez la première étape de la feuille de route.")}
            </p>
          )}
          {milestones.map((m, i) => (
            <div key={m.id}
              draggable
              onDragStart={() => { dragIndex.current = i; }}
              onDragOver={(e) => { e.preventDefault(); setOverIndex(i); }}
              onDragLeave={() => setOverIndex(null)}
              onDrop={(e) => { e.preventDefault(); move(dragIndex.current, i); dragIndex.current = null; setOverIndex(null); }}
              onDragEnd={() => { dragIndex.current = null; setOverIndex(null); }}
              className={cn("rounded-xl border bg-secondary/30 p-3 transition-colors",
                overIndex === i ? "border-athlynx/60" : "border-border")}
              data-testid={`milestone-card-${i}`}>
              <div className="flex items-start gap-2">
                <span className="cursor-grab active:cursor-grabbing text-muted-foreground/60 pt-2" aria-hidden>
                  <GripVertical className="h-4 w-4" />
                </span>
                <div className="flex-1 min-w-0 space-y-2.5">
                  <div className="flex items-center gap-2">
                    <Input value={m.title} onChange={(e) => patch(i, "title", e.target.value)}
                      placeholder={tfr("Titre du jalon…")} className="h-9 text-sm flex-1" data-testid={`milestone-title-${i}`} />
                    <div className="flex items-center gap-1 shrink-0">
                      <span className="text-xs text-muted-foreground">M+</span>
                      <Input type="number" min={0} max={120} value={m.target_month ?? ""} aria-label={tfr("Échéance en mois")}
                        onChange={(e) => patch(i, "target_month", e.target.value === "" ? 0 : parseInt(e.target.value, 10))}
                        className="h-9 w-16 text-sm tabular-nums" data-testid={`milestone-month-${i}`} />
                    </div>
                  </div>
                  <div className="flex items-center flex-wrap gap-x-3 gap-y-2">
                    <div className="flex items-center gap-1" role="group" aria-label={tfr("Statut")}>
                      {Object.entries(MS_STATUS).map(([sid, meta]) => (
                        <button key={sid} type="button" onClick={() => patch(i, "status", sid)}
                          aria-pressed={m.status === sid} data-testid={`milestone-status-${i}-${sid}`}
                          className={cn("rounded-full border px-2 h-7 text-[11px] transition-colors",
                            m.status === sid ? "text-white border-transparent" : "border-border text-muted-foreground hover:text-foreground")}
                          style={m.status === sid ? { background: meta.hex } : undefined}>
                          {meta.label}
                        </button>
                      ))}
                    </div>
                    <label className="flex items-center gap-1.5 text-xs cursor-pointer select-none">
                      <Checkbox checked={!!m.is_key} onCheckedChange={(v) => patch(i, "is_key", !!v)}
                        data-testid={`milestone-key-${i}`} />
                      <Star className={cn("h-3.5 w-3.5", m.is_key ? "text-warning" : "text-muted-foreground/50")} />
                      {tfr("Jalon clé")}
                    </label>
                  </div>
                </div>
                <div className="flex flex-col gap-0.5 shrink-0">
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => move(i, i - 1)}
                    disabled={i === 0} aria-label={tfr("Monter")} data-testid={`milestone-up-${i}`}><ChevronUp className="h-3.5 w-3.5" /></Button>
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => move(i, i + 1)}
                    disabled={i === milestones.length - 1} aria-label="Descendre" data-testid={`milestone-down-${i}`}><ChevronDown className="h-3.5 w-3.5" /></Button>
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-danger/80 hover:text-danger"
                    onClick={() => setList(milestones.filter((_, k) => k !== i))}
                    aria-label={tfr("Supprimer le jalon")} data-testid={`milestone-delete-${i}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </WsSection>
    </div>
  );
}

/* ---------- STEP 4 — Objectives (cards, slider, priority) ---------- */
export function StepObjectives({ draft, update }) {
  const goals = draft.goals || [];
  const setList = (list) => update(["goals"], list);
  const patch = (i, key, val) => setList(goals.map((g, k) => (k === i ? { ...g, [key]: val } : g)));

  return (
    <div data-testid="builder-step-objectives">
      <WsSection title="Objectifs" subtitle={tfr("Progression, responsable et priorité — la couleur s'adapte automatiquement.")}
        right={
          <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs"
            onClick={() => setList([...goals, { id: uid("bg"), title: "", owner_type: "agent", due_date: "", progress: 0, priority: "medium" }])}
            data-testid="objective-add-btn">
            <Plus className="h-3.5 w-3.5" /> Ajouter un objectif
          </Button>
        }>
        <div className="space-y-2.5">
          {goals.length === 0 && (
            <p className="text-sm text-muted-foreground rounded-xl border border-dashed border-border p-4 text-center">
              {tfr("Aucun objectif — ajoutez les priorités des prochains mois.")}
            </p>
          )}
          {goals.map((g, i) => (
            <div key={g.id} className="rounded-xl border border-border bg-secondary/30 p-3 space-y-2.5"
              style={{ borderLeft: `3px solid ${PRIORITY_META[g.priority]?.dot || "#F59E0B"}` }}
              data-testid={`objective-card-${i}`}>
              <div className="flex items-center gap-2">
                <Input value={g.title} onChange={(e) => patch(i, "title", e.target.value)}
                  placeholder={tfr("Titre de l'objectif…")} className="h-9 text-sm flex-1" data-testid={`objective-title-${i}`} />
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-danger/80 hover:text-danger shrink-0"
                  onClick={() => setList(goals.filter((_, k) => k !== i))}
                  aria-label={tfr("Supprimer l'objectif")} data-testid={`objective-delete-${i}`}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
              <div className="flex items-center flex-wrap gap-x-4 gap-y-2">
                <div className="flex items-center gap-1" role="group" aria-label="Responsable">
                  {OWNER_OPTIONS.map((o) => (
                    <button key={o.id} type="button" onClick={() => patch(i, "owner_type", o.id)}
                      aria-pressed={g.owner_type === o.id} data-testid={`objective-owner-${i}-${o.id}`}
                      className={cn("rounded-full border px-2.5 h-7 text-[11px] transition-colors",
                        g.owner_type === o.id ? "bg-athlynx text-white border-athlynx" : "border-border text-muted-foreground hover:text-foreground")}>
                      {o.label}
                    </button>
                  ))}
                </div>
                <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Calendar className="h-3.5 w-3.5" aria-hidden />
                  <input type="date" value={g.due_date || ""} onChange={(e) => patch(i, "due_date", e.target.value)}
                    className="h-8 rounded-md border border-border bg-background px-2 text-xs text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-athlynx/40"
                    aria-label={tfr("Échéance")} data-testid={`objective-due-${i}`} />
                </label>
                <div className="flex items-center gap-1" role="group" aria-label={tfr("Priorité")}>
                  {Object.entries(PRIORITY_META).map(([pid, meta]) => (
                    <button key={pid} type="button" onClick={() => patch(i, "priority", pid)}
                      aria-pressed={g.priority === pid} data-testid={`objective-priority-${i}-${pid}`}
                      className={cn("rounded-full border px-2.5 h-7 text-[11px] transition-colors",
                        g.priority === pid ? meta.cls : "border-border text-muted-foreground hover:text-foreground")}>
                      {meta.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Slider value={[g.progress || 0]} min={0} max={100} step={5}
                  onValueChange={([v]) => patch(i, "progress", v)}
                  className="flex-1" aria-label="Progression" data-testid={`objective-progress-${i}`} />
                <span className="text-xs font-semibold tabular-nums w-10 text-right" data-testid={`objective-progress-value-${i}`}>
                  {g.progress || 0}%
                </span>
              </div>
            </div>
          ))}
        </div>
      </WsSection>
    </div>
  );
}
